import React, { useEffect, useRef, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { darkTheme, defaultTheme } from "config/theme.config";
import Loading from "components/Loading";
import ErrorBoundary from "components/ErrorBoundary";
import router from "routes";
import useMediaQuery from "hooks/useMediaQuery";
import useDetectDevTools from "hooks/useDetectDevTools";
import elearningApi from "api/elearningApi";
import CapSuspendedOverlay from "features/elearning/components/CapSuspendedOverlay";

// 1 sessionId per tab — giúp admin gom event cùng phiên trong audit log.
const _devtoolsSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Công tắc tổng cho lớp bảo vệ nội dung học: chống DevTools (auto-logout)
// + chặn chuột phải / nhấn-giữ. BẬT ở production. Chỉ đặt `false` TẠM THỜI
// khi cần remote-debug mobile (vd lấy link ảnh để chẩn đoán) rồi nhớ bật lại.
const PROTECTION_ENABLED = true;

const App = () => {
  const [theme, setTheme] = useState(defaultTheme);
  const countdownRef = useRef(null);
  const isProduction = process.env.REACT_APP_ENV === "production";

  useDetectDevTools(({ method, viewport }) => {
    // Công tắc PROTECTION_ENABLED: khi tắt thì KHÔNG logout lúc mở DevTools
    // (phục vụ remote-debug). Return false để hook không latch → bật lại flag
    // là detect bình thường.
    if (!PROTECTION_ENABLED) return false;

    // Skip toàn bộ logout flow nếu HV chưa đăng nhập (không có session
    // token + không có moodle token). Lý do: trang login bị false-positive
    // (vd dimension diff trên một số WebView/zoom level) → callback chạy
    // → clear localStorage + reload → land lại login → fire lại → reload
    // loop. HV chưa login chưa có gì để cắt; cũng không cần audit log.
    // Return false để hook KHÔNG latch firedRef → cho phép detection
    // hoạt động lại sau khi HV đăng nhập trong cùng tab.
    const hasSessionToken = !!localStorage.getItem('sessionToken');
    const hasMoodleToken = !!localStorage.getItem('moodleToken');
    if (!hasSessionToken && !hasMoodleToken) {
      if (!isProduction) {
        console.log('[devtools detect] skipped — no auth token (login page)');
      }
      return false;
    }

    // Log backend (best-effort — không chặn UX nếu fail)
    try {
      elearningApi.logDevtoolsEvent({
        method, viewport, sessionId: _devtoolsSessionId,
        url: window.location.href,
      }).catch(() => { /* noop */ });
    } catch (_) { /* noop */ }

    if (!isProduction) {
      console.log('[devtools detect] dev mode — method:', method);
      return;
    }

    console.log(
      '%c⛔ CẢNH BÁO BẢO MẬT!',
      'color: red; font-size: 30px; font-weight: bold; text-shadow: 1px 1px black;'
    );
    console.log(
      '%cHệ thống đã ghi nhận bạn truy cập DevTools.',
      'color: orange; font-size: 18px; font-weight: bold;'
    );
    console.log(
      '%cTài khoản của bạn sẽ bị đăng xuất và sự việc sẽ được báo cáo cho quản trị viên trung tâm.',
      'color: black; font-size: 16px; background-color: yellow; padding: 4px;'
    );

    let secondsLeft = 5;
    countdownRef.current = setInterval(() => {
      console.log(`%c⏳ Đăng xuất sau: ${secondsLeft--} giây`, 'color: red; font-size: 16px;');
      if (secondsLeft < 0) {
        clearInterval(countdownRef.current);
        try {
          // Force logout — clear toàn bộ phiên rồi reload (auth guard
          // sẽ redirect về /elearning/login)
          localStorage.removeItem('moodleToken');
          localStorage.removeItem('moodleSiteInfo');
          localStorage.removeItem('forcePasswordChange');
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('rc-auth-token');
          localStorage.removeItem('rc-user-id');
          localStorage.removeItem('rc-username');
          localStorage.removeItem('rc-channel-id');
          localStorage.removeItem('elearning_limit_blocked');
        } catch (_) { /* noop */ }
        try { window.location.reload(); } catch (_) { /* noop */ }
      }
    }, 1000);
  });

  useEffect(() => {
    // Chặn chuột phải / nhấn-giữ + phím DevTools. Tắt qua PROTECTION_ENABLED
    // khi cần long-press copy link (debug). Chỉ áp ở production.
    if (PROTECTION_ENABLED && isProduction) {
      const handleContextMenu = (e) => e.preventDefault();
      const handleKeyDown = (e) => {
        if (e.key === "F12" || (e.ctrlKey && e.shiftKey && e.key === "I")) {
          e.preventDefault();
        }
      };

      document.addEventListener("contextmenu", handleContextMenu);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("contextmenu", handleContextMenu);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, []);

  const [customColor, setCustomColor] = useState('#0d6efd');

  // Apply CSS variables
  const applyTheme = (themeObj) => {
    document.documentElement.setAttribute('data-bs-theme', themeObj.name);
    Object.entries(themeObj.variables).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  };

  // Load theme from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('app-theme');
    if (stored) {
      const parsed = JSON.parse(stored);
      setTheme(parsed);
      applyTheme(parsed);
    } else {
      applyTheme(defaultTheme);
    }
  }, []);

  // Save to localStorage when theme changes
  useEffect(() => {
    localStorage.setItem('app-theme', JSON.stringify(theme));
  }, [theme]);

  const switchTheme = (type) => {
    const newTheme = type === 'dark' ? darkTheme : defaultTheme;
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  const updateCustomPrimary = (color) => {
    const newTheme = {
      ...theme,
      name: 'custom',
      variables: {
        ...theme.variables,
        '--bs-primary': color.hex,
      },
    };
    setCustomColor(color.hex);
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  return (
    <ErrorBoundary>
      <RouterProvider router={router} fallbackElement={<Loading />} />
      <ToastContainer />
      <CapSuspendedOverlay />
    </ErrorBoundary>
  );
};

export default App;
